import type { PieceSpec } from './types';
import type { DifficultyLevel } from './grid';

/** 运行时碎片：静态规格 + 拖拽/旋转/吸附状态。 */
export interface RuntimePiece extends PieceSpec {
  /** 当前主体左上角（canvas 世界坐标）。 */
  currentX: number;
  currentY: number;
  /** 0/90/180/270。 */
  rotation: number;
  /** 是否已吸附锁定（不可再移动）。 */
  placed: boolean;
  /** 渲染层级；越大越在上。 */
  zIndex: number;
}

export type Theme = 'cozy' | 'dark' | 'light';

export type GameStatus = 'idle' | 'ready' | 'playing' | 'paused' | 'completed';

export interface BestRecord {
  timeMs: number;
  moves: number;
}

/** 撤销历史条目：一次拖拽开始前该块的快照。 */
export interface HistoryEntry {
  id: number;
  x: number;
  y: number;
  rot: number;
  placed: boolean;
}

/** 用户可配置项。 */
export interface GameConfig {
  difficulty: DifficultyLevel;
  rotateMode: boolean; // 默认开
  showReference: boolean;
  referenceOpacity: number; // 0..1
}

export interface PreparedImageRef {
  canvas: HTMLCanvasElement | OffscreenCanvas;
  width: number;
  height: number;
}

export interface GameState {
  status: GameStatus;
  image: PreparedImageRef | null;
  /** 用于本地最佳记录 key（图片标识）。 */
  imageKey: string;
  config: GameConfig;
  pieces: RuntimePiece[];
  puzzleSeed: number;
  pieceWidth: number;
  pieceHeight: number;
  /** 画板在 canvas 中的位置与尺寸。 */
  boardX: number;
  boardY: number;
  boardW: number;
  boardH: number;
  trayX: number;
  trayY: number;
  trayW: number;
  trayH: number;
  /** 计时。 */
  startedAt: number | null;
  elapsedMs: number;
  paused: boolean;
  /** 计步 / 进度。 */
  moves: number;
  placedCount: number;
  /** 交互态。 */
  draggingId: number | null;
  dragOffsetX: number;
  dragOffsetY: number;
  /** 当前最大 zIndex（用于置顶）。 */
  topZ: number;
  /** 提示：剩余次数 / 当前高亮目标 / 高亮截止时间戳。 */
  hintsLeft: number;
  hintTargetId: number | null;
  hintUntil: number;
  /** 撤销历史栈。 */
  history: HistoryEntry[];
  /** 静音。 */
  muted: boolean;
  /** 主题。 */
  theme: Theme;
  /** 游戏内容缩放（滚轮控制）。 */
  zoom: number;
  /** 托盘滚动偏移 / 最大可滚动量。 */
  trayScroll: number;
  maxTrayScroll: number;
  /** 托盘碎片缩略比例（<1，使托盘多列；画板仍全尺寸无损）。 */
  trayScale: number;
}

export type Listener = (state: GameState) => void;

/**
 * 集中式状态 store。
 *
 * 设计：内部状态可变；渲染主循环每帧直接读 `get()`（拖拽高频更新无需通知）；
 * 只有"语义事件"（吸附、完成、状态切换、计时 tick）后才手动 `emit()` 通知 UI 控件。
 * 这样既保证拖拽 60fps，又让工具栏等离散 UI 能订阅更新。
 */
export class Store {
  private _state: GameState;
  private readonly listeners = new Set<Listener>();

  constructor(initial: GameState) {
    this._state = initial;
  }

  get(): GameState {
    return this._state;
  }

  /** 直接修改状态（不触发通知）；渲染循环始终读到最新值。 */
  mutate(fn: (s: GameState) => void): void {
    fn(this._state);
  }

  /** 通知所有订阅者（用于离散 UI 更新）。 */
  emit(): void {
    const s = this._state;
    for (const l of this.listeners) l(s);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}
